import { useEffect } from 'react';
import type { Tso } from 'src/apis/monolith/types';
import { isServerError, useAxios } from 'src/hooks/use-axios';
import { NotFound } from 'src/utils/errors';
import type { NoContent } from 'src/utils/no-content';

export type CommunicationInfo = {
  phone?: string;
  phoneBackup?: string;
  phoneOnCall?: string;
  mobile?: string;
  fax?: string;
  email?: string;
};

export type ContactType =
  | 'DISPATCHING'
  | 'BILLING'
  | 'COMMUNICATION'
  | 'OPERATION';

export type ContactPerson = {
  id: number;
  type?: ContactType;
  salutation?: 'MR' | 'MRS';
  title?: string;
  section?: string;
  firstName?: string;
  lastName?: string;
  communication?: CommunicationInfo;
};

export function useTso(id: string | number) {
  const tso = useAxios(
    (axios, baseConfig, id: string | number) =>
      axios.request<Tso>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.monolithApiUrl}/tso/${id}`,
        method: 'GET',
        validateStatus(status) {
          // sadly we get a 204 in case of an unknown id
          // see https://prisma.atlassian.net/browse/ISR-1819?focusedCommentId=127331
          if (status === 204) return false;
          return status >= 200 && status < 300; // default
        },
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        // see `validateStatus`
        if (isServerError(error, 204)) {
          throw new NotFound();
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    tso.execute(id);
    // oxlint-disable-next-line exhaustive-deps
  }, [id]);

  return tso;
}

export type TsoRequest = ReturnType<typeof useTso>;

export function useOptionalTso(id?: string | number) {
  const tso = useAxios(
    (axios, baseConfig, id: string | number) =>
      // when organisation is not known to the monolith, we get a valid response with 204
      // but it has no content. these types are exactly what we get from BE
      axios.request<Tso | NoContent>({
        ...baseConfig,
        url: `${PRISMA_CONFIG.monolithApiUrl}/tso/${id}`,
        method: 'GET',
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!id) return;
    tso.execute(id);
    // oxlint-disable-next-line exhaustive-deps
  }, [id]);

  if (!id) return;

  return tso;
}

export type TsoOptionalRequest = ReturnType<typeof useOptionalTso>;
